import { getAPIURL } from "./config.js";

// Экспортируем getAPIURL для обратной совместимости
export { getAPIURL };

// Утилитарная функция для правильной обработки ошибок HTTP ответов
export const handleResponseError = async (
  response,
  defaultMessage = "Ошибка сервера",
) => {
  if (!response.ok) {
    // Сначала проверим, является ли ответ JSON
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || errorData.message || defaultMessage,
        );
      } catch (jsonError) {
        // Если не удалось распарсить JSON, используем общее сообщение
        throw new Error(
          `${defaultMessage}: ${response.status} ${response.statusText}`,
        );
      }
    } else {
      // Если ответ не JSON, получаем текст ошибки
      try {
        const errorText = await response.text();
        throw new Error(`${defaultMessage}: ${response.status} ${errorText}`);
      } catch (textError) {
        // Если не удалось прочитать текст, используем общее сообщение
        throw new Error(
          `${defaultMessage}: ${response.status} ${response.statusText}`,
        );
      }
    }
  }
  return response;
};

export const aqFetch = (path, params) => {
  return fetch(`${getAPIURL()}${path}`, {
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    ...params,
  });
};

export const login = async (username, password) => {
  try {
    console.log("Attempting to login...");
    const response = await fetch(`${getAPIURL()}auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, hashed_password: password }),
      credentials: "include",
    });

    console.log("Response received:", response);
    console.log("Cookies after login:", document.cookie);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Login response received");

    return data;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${getAPIURL()}auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
      credentials: "include",
    });

    if (!response.ok) {
      // Пытаемся получить JSON ошибки, если не получается - текст
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Ошибка HTTP: ${response.status}`,
        );
      } else {
        const errorText = await response.text();
        throw new Error(`Ошибка HTTP: ${response.status} ${errorText}`);
      }
    }

    // Согласно новому контракту, ответ - строка, а не JSON
    const responseText = await response.text();
    return responseText;
  } catch (error) {
    console.error("Ошибка при регистрации:", error);
    throw error;
  }
};

export const fetchUserDetails = async () => {
  try {
    const response = await fetch(`${getAPIURL()}auth/me?_t=${Date.now()}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error(
        `Ошибка при получении данных пользователя: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    // Проверяем новую структуру ответа
    if (data.users && Array.isArray(data.users) && data.users.length > 0) {
      // Возвращаем первого пользователя из массива
      return data.users[0];
    } else if (data.users && Array.isArray(data.users)) {
      // Если массив пустой, возвращаем null или пустой объект
      return null;
    } else {
      // Если структура старая, возвращаем данные как есть
      return data;
    }
  } catch (error) {
    console.error("Ошибка при получении данных пользователя", error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await fetch(`${getAPIURL()}auth/logout`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Ошибка при выходе из системы");
    }

    // Очищаем данные пользователя из localStorage
    localStorage.removeItem("userData");
    localStorage.removeItem("accessLevel");
    // Очистите все остальные данные, связанные с пользователем

    return await response.json();
  } catch (error) {
    console.error("Ошибка при выходе из системы:", error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    let allUsers = [];
    let page = 1;
    const pageSize = 100; // Можно увеличить, если сервер поддерживает большее значение
    let hasMoreUsers = true;

    while (hasMoreUsers) {
      const response = await fetch(
        `${getAPIURL()}auth/users?page=${page}&page_size=${pageSize}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Ошибка при получении пользователей:", errorData);
        throw new Error(
          `Ошибка при получении пользователей: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Проверяем новую структуру ответа
      if (data.users && Array.isArray(data.users)) {
        allUsers = allUsers.concat(data.users);
        hasMoreUsers = data.users.length === pageSize;
      } else if (Array.isArray(data)) {
        // Если структура старая (прямой массив)
        allUsers = allUsers.concat(data);
        hasMoreUsers = data.length === pageSize;
      } else {
        console.error("Неожиданный формат данных:", data);
        hasMoreUsers = false;
      }

      page++;
    }

    return allUsers;
  } catch (error) {
    console.error("Ошибка при получении всех пользователей", error);
    throw error;
  }
};

export const getAllGroups = async (
  departmentId = null,
  groupId = null,
  isActive = true,
) => {
  try {
    const params = new URLSearchParams();
    if (departmentId !== null) {
      params.append("department_id", departmentId);
    }
    if (groupId !== null) {
      params.append("group_id", groupId);
    }
    if (isActive !== undefined) {
      params.append("is_active", isActive);
    }

    const url = `${getAPIURL()}groups/all?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении всех отделов");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех отделов", error);
    throw error;
  }
};

export const getAllGroupsWithRoles = async (groupId = null) => {
  try {
    const params = new URLSearchParams();
    if (groupId !== null) {
      params.append("group_id", groupId);
    }

    const url = `${getAPIURL()}groups/all_with_roles?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении групп с ролями");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении групп с ролями", error);
    throw error;
  }
};

export const getAllSections = async (groupId = null, isActive = true) => {
  try {
    const params = new URLSearchParams();
    if (groupId !== null) {
      params.append("group_id", groupId);
    }
    if (isActive !== undefined) {
      params.append("is_active", isActive);
    }

    const url = `${getAPIURL()}sections/all?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении всех секций");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех секций", error);
    throw error;
  }
};

export const getAllRoles = async (groupId = null, isActive = true) => {
  try {
    const params = new URLSearchParams();
    if (groupId !== null) {
      params.append("group_id", groupId);
    }
    if (isActive !== undefined) {
      params.append("is_active", isActive);
    }

    const url = `${getAPIURL()}roles/all?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении всех ролей");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех ролей", error);
    throw error;
  }
};

/**
 * Создание роли с привязкой к отделам (groups).
 * POST /roles/add
 * @param {{ name: string, group_ids: number[] }} data
 * @returns {Promise<{ id: number, name: string, is_active: boolean }>}
 */
export const createRole = async (data) => {
  try {
    const url = `${getAPIURL()}roles/add`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: data.name,
        is_active: true,
        group_ids: data.group_ids || [],
      }),
    });

    if (!response.ok) {
      await handleResponseError(response, "Ошибка при создании роли");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании роли", error);
    throw error;
  }
};

// Функции для извлечения последних значений из истории
export const getLatestValueFromHistory = (history, fieldName) => {
  if (!history || !Array.isArray(history) || history.length === 0) {
    return null;
  }

  // Сортируем по дате эффективности (самая поздняя первая), при равной дате — по id (больший id = новее)
  const sortedHistory = history
    .filter((item) => item.effective_date !== null)
    .sort((a, b) => {
      const byDate = new Date(b.effective_date) - new Date(a.effective_date);
      if (byDate !== 0) return byDate;
      return (b.id ?? 0) - (a.id ?? 0);
    });

  return sortedHistory.length > 0 ? sortedHistory[0] : null;
};

export const getLatestSalaryFromHistory = (salaryHistory) => {
  const latest = getLatestValueFromHistory(salaryHistory, "salary_amount");
  return latest ? latest.salary_amount : null;
};

export const getLatestStatusFromHistory = (statusHistory) => {
  const latest = getLatestValueFromHistory(statusHistory, "status");
  return latest ? latest.status : null;
};

export const getLatestGroupFromHistory = (groupHistory) => {
  const latest = getLatestValueFromHistory(groupHistory, "group");
  return latest ? latest.group : null;
};

export const getLatestRoleFromHistory = (roleHistory) => {
  const latest = getLatestValueFromHistory(roleHistory, "primary_role");
  return latest ? latest.primary_role : null;
};

export const getLatestSectionFromHistory = (sectionHistory) => {
  const latest = getLatestValueFromHistory(sectionHistory, "primary_section");
  return latest ? latest.primary_section : null;
};

export const getLatestTeamLeaderFromHistory = (teamLeaderHistory) => {
  const latest = getLatestValueFromHistory(
    teamLeaderHistory,
    "primary_team_leader",
  );
  return latest ? latest.primary_team_leader : null;
};

export const getLatestGradeFromHistory = (gradeHistory) => {
  const latest = getLatestValueFromHistory(gradeHistory, "grade");
  return latest ? latest.grade : null;
};

export const getLatestWorkFormatFromHistory = (workFormatHistory) => {
  const latest = getLatestValueFromHistory(workFormatHistory, "work_format");
  return latest ? latest.work_format : null;
};

export const getLatestWorkStartTimeFromHistory = (workStartTimeHistory) => {
  const latest = getLatestValueFromHistory(
    workStartTimeHistory,
    "work_start_time",
  );
  return latest ? latest.work_start_time : null;
};

export const getLatestWorkHoursPerDayFromHistory = (workHoursPerDayHistory) => {
  const latest = getLatestValueFromHistory(
    workHoursPerDayHistory,
    "work_hours_per_day",
  );
  return latest ? latest.work_hours_per_day : null;
};

// Функция для поиска даты конкретной зарплаты в истории
export const getSalaryDateFromHistory = (salaryHistory, currentSalary) => {
  if (
    !salaryHistory ||
    !Array.isArray(salaryHistory) ||
    salaryHistory.length === 0
  ) {
    return null;
  }

  // Фильтруем записи с таким же значением зарплаты и непустой датой
  const matchingEntries = salaryHistory.filter(
    (item) =>
      item.salary_amount === currentSalary && item.effective_date !== null,
  );

  if (matchingEntries.length === 0) {
    return null;
  }

  // Сортируем по дате эффективности (самая поздняя дата первая)
  const sortedEntries = matchingEntries.sort(
    (a, b) => new Date(b.effective_date) - new Date(a.effective_date),
  );

  // Возвращаем дату самой поздней записи
  return sortedEntries[0].effective_date;
};

export const getUserById = async (userId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}auth/users?user_id=${userId}&_t=${Date.now()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Ошибка при получении информации о пользователе");
    }

    const data = await response.json();

    // Проверяем новую структуру ответа
    if (data.users && Array.isArray(data.users) && data.users.length > 0) {
      const user = data.users[0];

      // Обрабатываем данные пользователя, извлекая последние значения из истории
      const processedUser = {
        ...user,
        // Извлекаем последние значения из истории
        // Для зарплаты используем оригинальное значение, а не из истории
        salary: user.salary,
        is_active:
          getLatestStatusFromHistory(user.user_status_history) !== null
            ? getLatestStatusFromHistory(user.user_status_history)
            : user.is_active,
        // Для групп, ролей и секций используем последние значения из истории
        latest_group: getLatestGroupFromHistory(user.user_group_history),
        latest_role: getLatestRoleFromHistory(user.user_primary_role_history),
        latest_section: getLatestSectionFromHistory(
          user.primary_section_history,
        ),
        latest_team_leader: getLatestTeamLeaderFromHistory(
          user.primary_team_leader_history,
        ),
        latest_grade: getLatestGradeFromHistory(user.user_grade_history),
        latest_work_format: getLatestWorkFormatFromHistory(
          user.work_format_history,
        ),
        latest_work_start_time: getLatestWorkStartTimeFromHistory(
          user.work_start_time_history,
        ),
        latest_work_hours_per_day: getLatestWorkHoursPerDayFromHistory(
          user.work_hours_per_day_history,
        ),
      };

      return processedUser;
    } else if (data.users && Array.isArray(data.users)) {
      // Если массив пустой, возвращаем null
      return null;
    } else {
      // Если структура старая, возвращаем данные как есть
      return data;
    }
  } catch (error) {
    console.error("Ошибка при получении информации о пользователе:", error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await fetch(`${getAPIURL()}auth/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
      credentials: "include",
    });

    if (!response.ok) {
      // Пытаемся получить JSON ошибки, если не получается - текст
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Ошибка при обновлении пользователя: ${response.status}`,
        );
      } else {
        const errorText = await response.text();
        throw new Error(
          `Ошибка при обновлении пользователя: ${response.status} ${errorText}`,
        );
      }
    }

    // Согласно новому контракту, ответ - строка, а не JSON
    const responseText = await response.text();
    return responseText;
  } catch (error) {
    console.error("Ошибка при обновлении пользователя:", error);
    throw error;
  }
};

export const updateUserImage = async (userId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await fetch(`${getAPIURL()}auth/${userId}/image`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      // Пытаемся получить JSON ошибки, если не получается - текст
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Ошибка при обновлении аватара: ${response.status}`,
        );
      } else {
        const errorText = await response.text();
        throw new Error(
          `Ошибка при обновлении аватара: ${response.status} ${errorText}`,
        );
      }
    }

    // Согласно контракту, ответ - строка
    const responseText = await response.text();
    return responseText;
  } catch (error) {
    console.error("Ошибка при обновлении аватара:", error);
    throw error;
  }
};

export const getAllCompanies = async (isActive = true) => {
  try {
    const params = new URLSearchParams();
    if (isActive !== undefined) {
      params.append("is_active", isActive);
    }

    const url = `${getAPIURL()}companies/all?${params.toString()}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении всех компаний: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех компаний", error);
    throw error;
  }
};

/**
 * Получение контрагентов с опциональными фильтрами.
 * @param {Object} params
 * @param {string} [params.counterparty_type] — тип: "legal_entity" | "physical_entity"
 * @param {string} [params.counterparty_side] — чей контрагент: "counterparty" | "our_company"
 */
export const getAllCounterparties = async (params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.counterparty_type) {
      searchParams.set("counterparty_type", params.counterparty_type);
    }
    if (params.counterparty_side) {
      searchParams.set("counterparty_side", params.counterparty_side);
    }
    const queryString = searchParams.toString();
    const url = `${getAPIURL()}partners/counterparties/get/all${queryString ? `?${queryString}` : ""}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении контрагентов: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении контрагентов", error);
    throw error;
  }
};

/** Загрузка изображения в images_service. POST /images_service/add */
export const uploadImageToStorage = async (imageFile) => {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const url = `${getAPIURL()}images_service/add`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при загрузке изображения: ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }

    return response.json();
  } catch (error) {
    console.error("Ошибка при загрузке изображения в хранилище", error);
    throw error;
  }
};

/** Формирует URL изображения из images_service по file_name */
export const getImageServiceUrl = (fileName) => {
  if (!fileName) return null;
  return `${getAPIURL()}images_service/${encodeURIComponent(fileName)}`;
};

/** Получение контрагента — юр. лицо. GET /partners/counterparties/get/legal_entity/{counterparty_id} */
export const getLegalEntityCounterparty = async (counterpartyId) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/get/legal_entity/${counterpartyId}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении контрагента (юр. лицо): ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при получении контрагента (юр. лицо)", error);
    throw error;
  }
};

/** Получение контрагента — физ. лицо. GET /partners/counterparties/get/physical_entity/{counterparty_id} */
export const getPhysicalEntityCounterparty = async (counterpartyId) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/get/physical_entity/${counterpartyId}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении контрагента (физ. лицо): ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при получении контрагента (физ. лицо)", error);
    throw error;
  }
};

export const createCounterparty = async (counterpartyData) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/create`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(counterpartyData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при создании контрагента: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при создании контрагента", error);
    throw error;
  }
};

/** Создание контрагента — физ. лицо (ИП, ГПХ). POST /partners/counterparties/create/physical_entity */
export const createPhysicalEntityCounterparty = async (data) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/create/physical_entity`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при создании контрагента (физ. лицо): ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при создании контрагента (физ. лицо)", error);
    throw error;
  }
};

/** Создание контрагента — юр. лицо (ТОО). POST /partners/counterparties/create/legal_entity */
export const createLegalEntityCounterparty = async (data) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/create/legal_entity`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при создании контрагента (юр. лицо): ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при создании контрагента (юр. лицо)", error);
    throw error;
  }
};

/** Получение всех банковских счетов контрагента. GET /partners/counterparties/bank_account/all/{counterparty_id} */
export const getCounterpartyBankAccounts = async (counterpartyId) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/bank_account/all/${counterpartyId}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при получении счетов: ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при получении банковских счетов контрагента", error);
    throw error;
  }
};

/** Добавление банковского счёта контрагенту. POST /partners/counterparties/bank_account/add/{counterparty_id} */
export const addCounterpartyBankAccount = async (counterpartyId, data) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/bank_account/add/${counterpartyId}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при добавлении счёта: ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при добавлении банковского счёта", error);
    throw error;
  }
};

/** Обновление банковского счёта контрагента. PUT /partners/counterparties/bank_account/update/{bank_account_id} */
export const updateCounterpartyBankAccount = async (bankAccountId, data) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/bank_account/update/${bankAccountId}`;
    const response = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при обновлении счёта: ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при обновлении банковского счёта", error);
    throw error;
  }
};

/** Обновление контрагента — юр. лицо. PUT /partners/counterparties/update/legal_entity/{counterparty_id} */
export const updateLegalEntityCounterparty = async (counterpartyId, data) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/update/legal_entity/${counterpartyId}`;
    const response = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при обновлении контрагента (юр. лицо): ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при обновлении контрагента (юр. лицо)", error);
    throw error;
  }
};

/** Обновление контрагента — физ. лицо. PUT /partners/counterparties/update/physical_entity/{counterparty_id} */
export const updatePhysicalEntityCounterparty = async (
  counterpartyId,
  data,
) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/update/physical_entity/${counterpartyId}`;
    const response = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const msg =
        errorData.detail ||
        errorData.message ||
        `Ошибка при обновлении контрагента (физ. лицо): ${response.status} ${response.statusText}`;
      throw new Error(
        Array.isArray(msg) ? msg.map((d) => d.msg || d).join(", ") : msg,
      );
    }
    return response.json();
  } catch (error) {
    console.error("Ошибка при обновлении контрагента (физ. лицо)", error);
    throw error;
  }
};

export const deleteCounterparty = async (counterpartyId) => {
  try {
    const url = `${getAPIURL()}partners/counterparties/delete/${counterpartyId}`;
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при удалении контрагента: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при удалении контрагента", error);
    throw error;
  }
};

// Legal Entity (Наши организации) API functions
export const getAllLegalEntities = async () => {
  try {
    const url = `${getAPIURL()}partners/legal_entity/get/all`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении организаций: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении организаций", error);
    throw error;
  }
};

export const getLegalEntityById = async (legalEntityId) => {
  try {
    const url = `${getAPIURL()}partners/legal_entity/get/${legalEntityId}`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении организации: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении организации", error);
    throw error;
  }
};

export const createLegalEntity = async (legalEntityData) => {
  try {
    const url = `${getAPIURL()}partners/legal_entity/create`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(legalEntityData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при создании организации: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при создании организации", error);
    throw error;
  }
};

export const updateLegalEntity = async (legalEntityId, legalEntityData) => {
  try {
    const url = `${getAPIURL()}partners/legal_entity/update/${legalEntityId}`;
    const response = await fetch(url, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(legalEntityData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при обновлении организации: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при обновлении организации", error);
    throw error;
  }
};

export const deleteLegalEntity = async (legalEntityId) => {
  try {
    const url = `${getAPIURL()}partners/legal_entity/delete/${legalEntityId}`;
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при удалении организации: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при удалении организации", error);
    throw error;
  }
};

export const getAllAccessLevels = async () => {
  try {
    const response = await fetch(`${getAPIURL()}access_levels/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении уровней доступа");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошика при получении уровней доступа", error);
    throw error;
  }
};

export const changeUserPassword = async (userId, username, newPassword) => {
  try {
    const requestData = {
      username: username,
      hashed_password: newPassword,
    };
    console.log("Отправляем данные для смены пароля");

    const response = await fetch(
      `${getAPIURL()}auth/change_login_password_access_level/${userId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Ответ сервера при ошибке:", errorData);
      throw new Error(errorData.message || `Ошибка HTTP: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при смене пароля пользователя:", error);
    throw error;
  }
};

/**
 * Обновление логина, пароля и/или уровней доступа пользователя.
 * @param {number} userId - id пользователя
 * @param {{ username?: string, hashed_password?: string, access_levels: number[] }} payload
 */
export const changeUserLoginPasswordAccessLevel = async (userId, payload) => {
  try {
    const requestData = {
      ...(payload.username !== undefined && { username: payload.username }),
      ...(payload.hashed_password !== undefined && {
        hashed_password: payload.hashed_password,
      }),
      access_levels: payload.access_levels ?? [],
    };

    const response = await fetch(
      `${getAPIURL()}auth/change_login_password_access_level/${userId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? Array.isArray(errorData.detail)
            ? errorData.detail.map((d) => d.msg).join(", ")
            : errorData.detail
          : `Ошибка HTTP: ${response.status}`,
      );
    }

    const text = await response.text();
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return text;
    }
  } catch (error) {
    console.error("Ошибка при обновлении уровня доступа пользователя:", error);
    throw error;
  }
};

export const getAllDepartments = async () => {
  try {
    const response = await fetch(`${getAPIURL()}departments/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Ответ сервера:", errorData);
      throw new Error(
        `Ошибка при получении всех отделов: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении всех отделов", error);
    throw error;
  }
};

export const getCalendar = async (dateFrom, dateTo) => {
  try {
    const response = await fetch(
      `${getAPIURL()}calendars/all?date_from=${dateFrom}&date_to=${dateTo}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Ошибка при получении данных календаря");
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении данных календаря:", error);
    throw error;
  }
};

export const getAccountingReportHours = async (
  groupId,
  userIds,
  dateFrom,
  dateTo,
  allUsers = false,
) => {
  try {
    let url = `${getAPIURL()}acc_exports/accounting_report_hours?`;

    const params = new URLSearchParams();
    if (groupId) params.append("group_id", groupId);
    if (userIds) {
      if (Array.isArray(userIds)) {
        userIds.forEach((id) => params.append("user_ids", id));
      } else {
        params.append("user_ids", userIds);
      }
    }
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (allUsers) params.append("all_users", "true");

    const fullUrl = `${url}${params.toString()}`;
    console.log("Запрос getAccountingReportHours:", fullUrl);

    const response = await fetch(fullUrl, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    console.log("Ответ getAccountingReportHours - статус:", response.status);
    console.log("Ответ getAccountingReportHours - данные:", data);
    console.log("Тип данных:", Array.isArray(data) ? "массив" : typeof data);
    if (Array.isArray(data) && data.length > 0) {
      console.log("Первый элемент данных:", data[0]);
    }

    if (!response.ok || data.error) {
      throw new Error(
        data.error || "Ошибка при получении отчета о рабочем времени",
      );
    }

    return Array.isArray(data) ? data : [data];
  } catch (error) {
    console.error("Ошибка при получении отчета о рабочем времени:", error);
    return []; // Возвращаем пустой массив вместо выброса ошибки
  }
};

export const getAccountingReportExcel = async (
  groupId,
  userIds,
  dateFrom,
  dateTo,
  overtimeType,
  allUsers = false,
) => {
  try {
    const params = new URLSearchParams();
    if (groupId) params.append("group_id", groupId);
    if (userIds) {
      if (Array.isArray(userIds)) {
        userIds.forEach((id) => params.append("user_ids", id));
      } else {
        params.append("user_ids", userIds);
      }
    }
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (overtimeType) params.append("overtime_type", overtimeType);
    if (allUsers) params.append("all_users", "true");

    const url = `${getAPIURL()}acc_exports/accounting_report_excel?${params.toString()}`;
    console.log("Запрос на выгрузку Excel:", url);

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("Статус ответа:", response.status, response.statusText);
    console.log(
      "Заголовки ответа:",
      Object.fromEntries(response.headers.entries()),
    );

    if (!response.ok) {
      // Пытаемся получить детальную информацию об ошибке от сервера
      let errorMessage = "";
      try {
        const contentType = response.headers.get("content-type");
        console.log("Content-Type ответа:", contentType);

        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          console.log("JSON ошибки от сервера:", errorData);
          errorMessage =
            errorData.detail || errorData.message || errorData.error || "";
        } else {
          const textResponse = await response.text();
          console.log("Текстовый ответ от сервера:", textResponse);
          errorMessage = textResponse;
        }
      } catch (e) {
        console.log("Ошибка при парсинге ответа:", e);
        errorMessage = "";
      }

      if (response.status === 504) {
        throw new Error(
          "Превышено время ожидания сервера. Попробуйте выбрать меньший период или количество сотрудников.",
        );
      } else if (response.status === 500) {
        const detailedMessage = errorMessage
          ? `Внутренняя ошибка сервера: ${errorMessage}`
          : "Внутренняя ошибка сервера. Обратитесь к администратору.";
        console.log("Финальное сообщение об ошибке 500:", detailedMessage);
        throw new Error(detailedMessage);
      } else {
        const detailedMessage = errorMessage
          ? `Ошибка при получении Excel отчета (${response.status}): ${errorMessage}`
          : `Ошибка при получении Excel отчета: ${response.status}`;
        console.log("Финальное сообщение об ошибке:", detailedMessage);
        throw new Error(detailedMessage);
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `Отчет_${dateFrom}_${dateTo}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Ошибка при получении Excel отчета:", error);
    throw error;
  }
};

export const getAccountingReportExcelV3 = async (
  groupId,
  userIds,
  dateFrom,
  dateTo,
  allUsers = false,
) => {
  try {
    const params = new URLSearchParams();
    if (groupId) params.append("group_id", groupId);
    if (userIds) {
      if (Array.isArray(userIds)) {
        userIds.forEach((id) => params.append("user_ids", id));
      } else {
        params.append("user_ids", userIds);
      }
    }
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (allUsers) params.append("all_users", "true");

    const url = `${getAPIURL()}acc_exports/accounting_report_excel_v3?${params.toString()}`;
    console.log("Запрос на выгрузку Excel v3:", url);

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("Статус ответа:", response.status, response.statusText);
    console.log(
      "Заголовки ответа:",
      Object.fromEntries(response.headers.entries()),
    );

    if (!response.ok) {
      // Пытаемся получить детальную информацию об ошибке от сервера
      let errorMessage = "";
      try {
        const contentType = response.headers.get("content-type");
        console.log("Content-Type ответа:", contentType);

        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          console.log("JSON ошибки от сервера:", errorData);
          const detail = errorData.detail;
          errorMessage =
            (Array.isArray(detail) ? detail[0]?.msg || detail[0] : detail) ||
            errorData.message ||
            errorData.error ||
            "";
        } else {
          const textResponse = await response.text();
          console.log("Текстовый ответ от сервера:", textResponse);
          errorMessage = textResponse;
        }
      } catch (e) {
        console.log("Ошибка при парсинге ответа:", e);
        errorMessage = "";
      }

      if (response.status === 504) {
        throw new Error(
          "Превышено время ожидания сервера. Попробуйте выбрать меньший период или количество сотрудников.",
        );
      } else if (
        response.status === 404 &&
        (errorMessage.includes("Нет данных") ||
          errorMessage.includes("нет данных"))
      ) {
        throw new Error(
          "Нет данных для формирования отчёта с историей зарплат за выбранный период. Проверьте отдел и период.",
        );
      } else if (response.status === 500) {
        const detailedMessage = errorMessage
          ? `Внутренняя ошибка сервера: ${errorMessage}`
          : "Внутренняя ошибка сервера. Обратитесь к администратору.";
        console.log("Финальное сообщение об ошибке 500:", detailedMessage);
        throw new Error(detailedMessage);
      } else {
        const detailedMessage = errorMessage
          ? `Ошибка при получении Excel отчета (${response.status}): ${errorMessage}`
          : `Ошибка при получении Excel отчета: ${response.status}`;
        console.log("Финальное сообщение об ошибке:", detailedMessage);
        throw new Error(detailedMessage);
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `Отчет_с_историей_зарплат_${dateFrom}_${dateTo}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Ошибка при получении Excel отчета:", error);
    throw error;
  }
};

export const getUsersSummary = async ({
  userId,
  groupId,
  isActive,
  excludeAccessLevels,
  page = 1,
  pageSize = 10,
} = {}) => {
  try {
    const params = new URLSearchParams();

    if (userId) params.append("user_id", userId);
    if (groupId) params.append("group_id", groupId);
    if (isActive !== undefined) params.append("is_active", isActive);
    if (excludeAccessLevels) {
      excludeAccessLevels.forEach((level) =>
        params.append("exclude_access_levels", level),
      );
    }
    params.append("page", page);
    params.append("page_size", pageSize);

    const response = await fetch(`${getAPIURL()}auth/users/summary?${params}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        "Ошибка при получении краткой информации о пользователях",
      );
    }

    const data = await response.json();

    // Проверяем новую структуру ответа
    if (data.users && Array.isArray(data.users)) {
      // Возвращаем объект с users и метаданными
      return {
        users: data.users,
        total_count: data.total_count,
        page: data.page,
        page_size: data.page_size,
      };
    } else if (Array.isArray(data)) {
      // Если структура старая (прямой массив)
      return {
        users: data,
        total_count: data.length,
        page: 1,
        page_size: data.length,
      };
    } else {
      // Если структура другая, возвращаем данные как есть
      return data;
    }
  } catch (error) {
    console.error(
      "Ошибка при получении краткой информации о пользователях:",
      error,
    );
    throw error;
  }
};

export const getEmployeePayStatements = async (
  userName,
  dateStart = null,
  dateEnd = null,
) => {
  try {
    if (!userName) {
      throw new Error("Отсутствует обязательный параметр userName");
    }

    const params = new URLSearchParams();
    params.append("users", userName);
    if (dateStart) {
      params.append("date_start", dateStart);
    }
    if (dateEnd) {
      params.append("date_end", dateEnd);
    }

    const response = await fetch(
      `${getAPIURL()}employee_pay_statements/all?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Ответ сервера:", errorText);
      throw new Error(
        `Ошибка при получении данных о выплатах: ${response.status} ${errorText}`,
      );
    }

    const data = await response.json();
    return data[0];
  } catch (error) {
    console.error("Ошибка при получении данных о выплатах:", error);
    throw error;
  }
};

export const createDayOff = async (dayOffData) => {
  try {
    const response = await fetch(`${getAPIURL()}days_off/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(dayOffData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании заявки на отгул:", error);
    throw error;
  }
};

export const getUserDayoffs = async (userId) => {
  try {
    const response = await fetch(`${getAPIURL()}days_off/${userId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении отгулов пользователя:", error);
    throw error;
  }
};

export const getUsedOvertimesForDayOff = async (dayOffId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}days_off/${dayOffId}/used-overtimes`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении использованных переработок:", error);
    throw error;
  }
};

export const getAllDaysOff = async () => {
  try {
    const response = await fetch(`${getAPIURL()}days_off/all`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении отгулов");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении отгулов:", error);
    throw error;
  }
};

export const deleteDayoff = async (dayoffId) => {
  try {
    const response = await fetch(`${getAPIURL()}days_off/${dayoffId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail ||
        errorData.message ||
        `Ошибка при удалении отгула: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    // Эндпоинт может вернуть пустой ответ или JSON
    const data = await response.json().catch(() => ({}));
    return data;
  } catch (error) {
    console.error("Ошибка при удалении отгула:", error);
    throw error;
  }
};

export const getOrgChartData = async () => {
  try {
    const response = await aqFetch("auth/users/org_chart", {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении данных оргсхемы");
    }

    const responseData = await response.json();
    console.log("Полученные данные оргсхемы:", responseData);

    if (!responseData || !responseData.org_chart_data) {
      throw new Error("Неверный формат данных оргсхемы");
    }

    const data = responseData.org_chart_data;
    console.log("Данные для преобразования:", data);

    // Создаем массив для хранения всех узлов
    const transformedData = [];

    // Добавляем директора как корневой узел
    if (data.director_id && data.director_name) {
      transformedData.push({
        id: String(data.director_id),
        pid: "",
        name: data.director_name,
        title: "Директор",
        img: data.director_image || "/assets/images/placeholder-avatar.png",
        tags: ["director"],
      });
    }

    // Добавляем департаменты
    if (data.departments && Array.isArray(data.departments)) {
      data.departments.forEach((dept) => {
        if (
          dept &&
          dept.department &&
          dept.department.department_id &&
          dept.department.department_name
        ) {
          // Добавляем департамент
          transformedData.push({
            id: `dept_${dept.department.department_id}`,
            pid: String(data.director_id),
            name: dept.department.department_name,
            title: dept.department.leader_name,
            img:
              dept.department.leader_image ||
              "/assets/images/placeholder-avatar.png",
            tags: ["department"],
          });

          // Добавляем заместителей департамента на том же уровне
          if (dept.deputies && Array.isArray(dept.deputies)) {
            dept.deputies.forEach((deputy) => {
              if (deputy && deputy.deputy_id && deputy.deputy_name) {
                transformedData.push({
                  id: `deputy_${deputy.deputy_id}_${dept.department.department_id}`,
                  pid: String(data.director_id),
                  name: deputy.deputy_name,
                  title: deputy.roles
                    ? deputy.roles.join(", ")
                    : "Заместитель руководителя",
                  img:
                    deputy.deputy_image ||
                    "/assets/images/placeholder-avatar.png",
                  tags: ["deputy", "hidden"],
                  departmentId: dept.department.department_id,
                });
              }
            });
          }

          // Добавляем группы
          if (dept.department.groups && Array.isArray(dept.department.groups)) {
            dept.department.groups.forEach((group) => {
              if (group && group.group_id && group.group_name) {
                // Добавляем группу
                transformedData.push({
                  id: `group_${group.group_id}`,
                  pid: `dept_${dept.department.department_id}`,
                  name: group.group_name,
                  title: group.leader_name,
                  img:
                    group.leader_image ||
                    "/assets/images/placeholder-avatar.png",
                  tags: ["group"],
                });

                // Добавляем секции
                if (group.sections && Array.isArray(group.sections)) {
                  group.sections.forEach((section) => {
                    if (section && section.section_id && section.section_name) {
                      // Добавляем секцию
                      transformedData.push({
                        id: `section_${section.section_id}`,
                        pid: `group_${group.group_id}`,
                        name: section.section_name,
                        title: section.leader_name,
                        img:
                          section.leader_image ||
                          "/assets/images/placeholder-avatar.png",
                        tags: ["section"],
                      });

                      // Добавляем сотрудников секции
                      if (
                        section.employees &&
                        Array.isArray(section.employees)
                      ) {
                        section.employees.forEach((emp) => {
                          if (emp && emp.employee_id && emp.employee_name) {
                            transformedData.push({
                              id: `emp_${emp.employee_id}`,
                              pid: `section_${section.section_id}`,
                              name: emp.employee_name,
                              title: emp.roles ? emp.roles.join(", ") : "",
                              img:
                                emp.employee_image ||
                                "/assets/images/placeholder-avatar.png",
                              tags: ["employee"],
                            });
                          }
                        });
                      }
                    }
                  });
                }
              }
            });
          }
        }
      });
    }

    console.log("Преобразованные данные:", transformedData);
    return transformedData;
  } catch (error) {
    console.error("Ошибка при получении данных оргсхемы:", error);
    throw error;
  }
};

export const getAllWorkFormats = async () => {
  try {
    const response = await fetch(`${getAPIURL()}discipline/all_work_formats`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Ошибка при получении форматов работы");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении форматов работы", error);
    throw error;
  }
};

export const getFinData = async (year) => {
  try {
    const response = await fetch(
      `${getAPIURL()}finansist/fin_data?year=${year}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      // Проверяем Content-Type перед парсингом JSON
      const contentType = response.headers.get("content-type");
      let errorMessage = `Ошибка HTTP: ${response.status}`;

      if (contentType && contentType.includes("application/json")) {
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Если не удалось распарсить JSON, используем стандартное сообщение
        }
      } else {
        // Если ответ не JSON (например, HTML страница с ошибкой 504)
        if (response.status === 504) {
          errorMessage =
            "Превышено время ожидания ответа от сервера (Gateway Timeout). Запрос может выполняться долго, попробуйте позже.";
        } else {
          try {
            const errorText = await response.text();
            // Пытаемся извлечь полезную информацию из HTML, если это возможно
            if (errorText.includes("504")) {
              errorMessage =
                "Превышено время ожидания ответа от сервера (Gateway Timeout). Запрос может выполняться долго, попробуйте позже.";
            }
          } catch (e) {
            // Игнорируем ошибку чтения текста
          }
        }
      }

      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении финансовых данных:", error);
    throw error;
  }
};

// Эндпоинты для дисциплины
export const getMyAttendanceMonth = async (year, month) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);
    params.append("month", month);

    const response = await fetch(
      `${getAPIURL()}discipline/my_attendance/month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении данных дисциплины за месяц:", error);
    throw error;
  }
};

export const getMyAttendance6Months = async (
  startYear,
  startMonth,
  endYear,
  endMonth,
) => {
  try {
    const params = new URLSearchParams();
    params.append("start_year", startYear);
    params.append("start_month", startMonth);
    params.append("end_year", endYear);
    params.append("end_month", endMonth);

    const response = await fetch(
      `${getAPIURL()}discipline/my_attendance/6month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении данных дисциплины за 6 месяцев:",
      error,
    );
    throw error;
  }
};

export const getMyAttendanceYear = async (year) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);

    const response = await fetch(
      `${getAPIURL()}discipline/my_attendance/year?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении данных дисциплины за год:", error);
    throw error;
  }
};

// Получение статистики посещаемости по группам за месяц
export const getGroupAttendancesMonth = async (year, month) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);
    params.append("month", month);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости групп за месяц:",
      error,
    );
    throw error;
  }
};

// Получение статистики посещаемости по группам за 6 месяцев
export const getGroupAttendances6Month = async (
  startYear,
  startMonth,
  endYear,
  endMonth,
) => {
  try {
    const params = new URLSearchParams();
    params.append("start_year", startYear);
    params.append("start_month", startMonth);
    params.append("end_year", endYear);
    params.append("end_month", endMonth);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/6month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости групп за 6 месяцев:",
      error,
    );
    throw error;
  }
};

// Получение статистики посещаемости по группам за год
export const getGroupAttendancesYear = async (year) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/year?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости групп за год:",
      error,
    );
    throw error;
  }
};

// === НОВЫЕ API ФУНКЦИИ ДЛЯ ГРУППОВОЙ ДИСЦИПЛИНЫ ===

// Получение статистики посещаемости группы за месяц
export const getGroupAttendanceMonth = async (groupId, year, month) => {
  try {
    console.log("getGroupAttendanceMonth вызвана с параметрами:", {
      groupId,
      year,
      month,
    });

    const params = new URLSearchParams();
    params.append("year", year);
    params.append("month", month);

    const baseURL = getAPIURL();
    console.log("baseURL из getAPIURL():", baseURL);
    const url = `${baseURL}discipline/group_attendances/${groupId}/month?${params.toString()}`;
    console.log("URL запроса:", url);

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("Ответ сервера:", response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Ошибка ответа:", errorData);
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    const data = await response.json();
    console.log("Данные получены:", data);
    return data;
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости группы за месяц:",
      error,
    );
    throw error;
  }
};

// Получение статистики посещаемости группы за 6 месяцев
export const getGroupAttendance6Month = async (
  groupId,
  startYear,
  startMonth,
  endYear,
  endMonth,
) => {
  try {
    const params = new URLSearchParams();
    params.append("start_year", startYear);
    params.append("start_month", startMonth);
    params.append("end_year", endYear);
    params.append("end_month", endMonth);

    const response = await fetch(
      `${getAPIURL()}discipline/group_attendances/${groupId}/6month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости группы за 6 месяцев:",
      error,
    );
    throw error;
  }
};

// Получение статистики посещаемости группы за год
export const getGroupAttendanceYear = async (groupId, year) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);

    const response = await fetch(
      `${getAPIURL()}discipline/group_attendances/${groupId}/year?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении статистики посещаемости группы за год:",
      error,
    );
    throw error;
  }
};

// === API ФУНКЦИИ ДЛЯ ДЕТАЛЬНЫХ ДАННЫХ СОТРУДНИКА ===

// Получение детальных данных сотрудника за месяц
export const getEmployeeAttendanceMonth = async (employeeId, year, month) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);
    params.append("month", month);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/${employeeId}/month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении детальных данных сотрудника за месяц:",
      error,
    );
    throw error;
  }
};

// Получение детальных данных сотрудника за 6 месяцев
export const getEmployeeAttendance6Month = async (
  employeeId,
  startYear,
  startMonth,
  endYear,
  endMonth,
) => {
  try {
    const params = new URLSearchParams();
    params.append("start_year", startYear);
    params.append("start_month", startMonth);
    params.append("end_year", endYear);
    params.append("end_month", endMonth);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/${employeeId}/6month?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении детальных данных сотрудника за 6 месяцев:",
      error,
    );
    throw error;
  }
};

// Получение детальных данных сотрудника за год
export const getEmployeeAttendanceYear = async (employeeId, year) => {
  try {
    const params = new URLSearchParams();
    params.append("year", year);

    const response = await fetch(
      `${getAPIURL()}discipline/employee_attendances/${employeeId}/year?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Ошибка HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Ошибка при получении детальных данных сотрудника за год:",
      error,
    );
    throw error;
  }
};

// Получение агрегированного отчета по часам сотрудника за выбранные месяцы/период
export const getEmployeeReportHours = async ({
  employeeId,
  year,
  month,
  dateFrom,
  dateTo,
}) => {
  try {
    if (!employeeId) {
      throw new Error("Отсутствует обязательный параметр employeeId");
    }

    const params = new URLSearchParams();

    if (year) {
      params.append("year", year);
    }
    if (month) {
      params.append("month", month);
    }
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}discipline/employee_report/${employeeId}/hours${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      let errorMessage = `Ошибка HTTP: ${response.status}`;

      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } else {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `${errorMessage} ${errorText}`;
          }
        }
      } catch (e) {
        // Если не удалось прочитать тело ошибки, оставляем базовое сообщение
      }

      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении отчета по часам сотрудника:", error);
    throw error;
  }
};
